import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CssBaseline from '@mui/material/CssBaseline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import WarningIcon from '@mui/icons-material/Warning';
import ScienceIcon from '@mui/icons-material/Science';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WaterIcon from '@mui/icons-material/Water';
import SensorsIcon from '@mui/icons-material/Sensors';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import PhotoIcon from '@mui/icons-material/Photo';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import { CommunityProvider, useCommunity } from '../context/CommunityContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Icon mapping for topics
const topicIcons = {
  chat: ChatIcon,
  warning: WarningIcon,
  science: ScienceIcon,
  lightbulb: LightbulbIcon,
  water: WaterIcon,
  sensors: SensorsIcon,
  agriculture: AgricultureIcon,
  photo: PhotoIcon,
};

function NewPostContent() {
  const location = useLocation();
  const navigate = useNavigate();

  const { forumTopics, canPost, isDemo, createPost } = useCommunity();

  const [title, setTitle] = React.useState('');
  const [content, setContent] = React.useState('');
  const [selectedTopic, setSelectedTopic] = React.useState('');
  const [tagInput, setTagInput] = React.useState('');
  const [tags, setTags] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  const basePath = location.pathname.startsWith('/demo') ? '/demo' : '/app';

  // Handle tag input
  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
      if (newTag && !tags.includes(newTag) && tags.length < 5) {
        setTags([...tags, newTag]);
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }
    if (!content.trim()) {
      setError('Please enter content for your post');
      return;
    }
    if (!selectedTopic) {
      setError('Please select a topic');
      return;
    }

    setSubmitting(true);
    try {
      const newPost = await createPost({
        title: title.trim(),
        content: content.trim(),
        topicId: selectedTopic,
        tags,
      });
      navigate(`${basePath}/forum/post/${newPost.id}`);
    } catch (err) {
      setError(err.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  // If in demo mode and can't post, show message
  if (!canPost) {
    return (
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '800px' }, mx: 'auto', p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`${basePath}/forum`)}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Back to Forum
        </Button>

        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Sign up to create posts
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 3 }}>
            {isDemo
              ? "You're viewing the forum in demo mode. Create an account to post questions and share your experiences with the community!"
              : 'Please log in to create a new post.'}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate('/signup')}
            >
              Sign Up
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
            >
              Log In
            </Button>
          </Stack>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '800px' }, mx: 'auto', p: 3 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`${basePath}/forum`)}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to Forum
      </Button>

      {/* Page Title */}
      <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif', mb: 3 }}>
        Create New Post
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card variant="outlined" sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Topic Selection */}
            <FormControl fullWidth>
              <InputLabel>Topic *</InputLabel>
              <Select
                value={selectedTopic}
                label="Topic *"
                onChange={(e) => setSelectedTopic(e.target.value)}
              >
                {forumTopics.map((topic) => {
                  const TopicIcon = topicIcons[topic.icon] || ChatIcon;
                  return (
                    <MenuItem key={topic.id} value={topic.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TopicIcon sx={{ fontSize: 20, color: `${topic.color}.main` }} />
                        <span>{topic.name}</span>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Title */}
            <TextField
              label="Title *"
              placeholder="What's your question or topic?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 200 }}
              helperText={`${title.length}/200 characters`}
            />

            {/* Content */}
            <TextField
              label="Content *"
              placeholder="Describe your question or topic in detail. Include any relevant information like your setup, what you've tried, etc."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              multiline
              rows={10}
              helperText="You can use basic formatting: **bold**, - bullet points, # headers"
            />

            {/* Tags */}
            <Box>
              <TextField
                label="Tags"
                placeholder="Add tags (press Enter to add)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                fullWidth
                disabled={tags.length >= 5}
                helperText={`${tags.length}/5 tags - helps others find your post`}
              />
              {tags.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => handleRemoveTag(tag)}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Guidelines */}
            <Alert severity="info">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Posting Guidelines:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
                <li>Be clear and specific in your question</li>
                <li>Include details about your setup (medium, nutrients, lighting, etc.)</li>
                <li>Share what you've already tried</li>
                <li>Be respectful and helpful to others</li>
              </Typography>
            </Alert>

            {/* Submit */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate(`${basePath}/forum`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SendIcon />}
                disabled={submitting}
              >
                {submitting ? 'Posting...' : 'Post'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Card>
    </Box>
  );
}

export default function NewForumPostPage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <CommunityProvider>
              <SideMenu />
              <AppNavbar />
              <Box
                component="main"
                sx={(theme) => ({
                  flexGrow: 1,
                  backgroundColor: theme.vars
                    ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                    : alpha(theme.palette.background.default, 1),
                  overflow: 'auto',
                  minHeight: '100vh',
                })}
              >
                <Stack
                  sx={{
                    pt: { xs: 10, md: 3 },
                    pb: 5,
                  }}
                >
                  <NewPostContent />
                </Stack>
              </Box>
            </CommunityProvider>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
